import * as React from 'react'
import { useLayoutEffect, useRef } from 'react'

import { useCalendarContext } from './context'
import { Day } from './Day'
import type { CalendarBlockData } from './types'

import styles from './Month.module.css'

type MonthProps = {
  weeks: CalendarBlockData[][]
  scrollContainerRef?: React.RefObject<HTMLDivElement>
  scrollToToday?: boolean
}

type MonthStyle = React.CSSProperties & {
  '--number-weeks'?: number
}

const Month: React.FC<MonthProps> = ({
  weeks,
  scrollContainerRef,
  scrollToToday = false,
}) => {
  const { items, currentTime } = useCalendarContext()
  const todayRowRef = useRef<HTMLTableRowElement>(null)
  const hasScrolledRef = useRef(false)

  useLayoutEffect(() => {
    if (
      !scrollToToday ||
      !items ||
      !todayRowRef.current ||
      hasScrolledRef.current
    ) {
      return
    }

    let cancelled = false
    queueMicrotask(() => {
      const container = scrollContainerRef?.current
      const row = todayRowRef.current
      if (cancelled || !container || !row) return

      const headerHeight = container.querySelector('thead')?.clientHeight ?? 0
      container.scrollTop +=
        row.getBoundingClientRect().top -
        container.getBoundingClientRect().top -
        headerHeight
      hasScrolledRef.current = true
    })

    return (): void => {
      cancelled = true
    }
  }, [items, scrollContainerRef, scrollToToday, weeks])

  return (
    <>
      {weeks
        .filter((week) => week[0])
        .map((week) => {
          const monthStyle: MonthStyle = { '--number-weeks': weeks.length }
          const isTodayRow =
            scrollToToday &&
            week.some(
              (day) => day.startTimestamp === currentTime.todayTimestamp,
            )

          return (
            <tr
              key={week[0].start.weekOfYear}
              ref={isTodayRow ? todayRowRef : undefined}
              className={styles.row}
              data-testid={`monthly-row-${week[0].start.weekOfYear}`}
              style={monthStyle}
            >
              {week.map((day) => (
                <Day key={day.start.toString()} day={day} />
              ))}
            </tr>
          )
        })}
    </>
  )
}

Month.displayName = 'Month'

const memoMonth = React.memo(Month)
export { memoMonth as Month }
